<template>
  <li v-if="visible" :class="[isFeatureEnabled ? 'order-1' : 'order-2']">
    <nuxt-link
      v-if="visible"
      :to="link"
      :class="[
        'w-full px-4 py-4 transition-all cursor-pointer leading-none text-sm block border-b dark:border-gray-800',
        active ? 'bg-primary-100/40 text-primary-800 active:text-primary-800 dark:bg-primary-800/10 dark:text-primary-400' : 'text-gray-700 dark:text-gray-200',
        isFeatureEnabled ? 'cursor-pointer hover:bg-primary-100 dark:hover:bg-gray-900/50' : 'cursor-default opacity-50'
      ]"
    >
      <div class="flex items-center space-x-3">
        <UIcon v-if="item.icon && item.icon.startsWith('lucide:')" :name="item.icon" class="size-4" :class="{ 'opacity-60' : !active }" />
        <o-icon v-else-if="item.icon" :icon="item.icon" :size="18" :class="{ 'opacity-60' : !active }" />
        <span class="mt-1">
          <span class="text-[0.8rem] font-medium">
            {{ $t(item.label) }}
          </span>
          <span v-if="item.info" class="block text-sm opacity-80 font-normal pt-3px leading-tight">
            {{ $t(item.info) }}
          </span>
        </span>
      </div>
    </nuxt-link>
  </li>
</template>

<script setup lang="ts">
const instance = getCurrentInstance()
const route = useRoute()

interface Props {
  item: Record<string, any>
}

const props = defineProps<Props>()

const link = ref('/')

const active = computed(() => {
  const path = route.path
  return path.includes(props.item.match) || path.includes(link.value)
})

const isUserOwner = computed(() => {
  const employee = instance?.proxy?.$auth.info.employee
  return employee.role.isRoleTypeOwner
})

const visible = computed(() => {
  return isGateEnabled.value && ((isGrantAllowed.value && isFeatureEnabled.value) || isUserOwner.value)
})

const isGrantAllowed = computed(() => {
  const item = props.item

  // Check require permissions of tabs
  if (item.tabs) {
    const tabs = item.tabs
    const allowed = Object.values(tabs).some((tab: any) => {
      const grants = tab.path ? instance?.proxy?.$grants.route(tab.path) : null

      if (!grants) {
        setLink(tab.path)
        return true
      }

      const allowed = instance?.proxy?.$grants.all(grants)

      if (allowed) {
        setLink(tab.path)
      }

      return allowed
    })

    return allowed
  }
  // Check required permissions of page
  const grants = item.path ? instance?.proxy?.$grants.route(item.path) : null

  if (!grants) {
    setLink(item.path)
    return true
  }

  const allowed = instance?.proxy?.$grants.all(grants)

  if (allowed) {
    setLink(item.path)
  }

  return allowed
})

const isFeatureEnabled = computed(() => {
  const item = props.item

  if (item.tabs) {
    const tabs = item.tabs
    const enabled = Object.values(tabs).some((tab: any) => {
      const features = tab.path ? instance?.proxy?.$features.route(tab.path) : null

      if (!features) {
        return true
      }

      const allowed = instance?.proxy?.$features.all(features)

      return allowed
    })

    return enabled
  }

  // Check required permissions of page
  const features = item.path ? instance?.proxy?.$features.route(item.path) : null

  if (!features) {
    return true
  }

  const allowed = instance?.proxy?.$features.all(features)

  return allowed
})

const isGateEnabled = computed(() => {
  const item = props.item

  if (item.tabs) {
    const tabs = item.tabs
    const enabled = Object.values(tabs).some((tab: any) => {
      const gates = tab.path ? instance?.proxy?.$gates.route(tab.path) : null

      if (!gates) {
        return true
      }

      const allowed = instance?.proxy?.$gates.all(gates)

      return allowed
    })

    return enabled
  }

  // Check required permissions of page
  const gates = item.path ? instance?.proxy?.$gates.route(item.path) : null

  if (!gates) {
    return true
  }

  const allowed = instance?.proxy?.$gates.all(gates)

  return allowed
})

function setLink(newLink: string) {
  link.value = newLink
}
</script>
